import { useEffect, useRef } from "react";
import * as THREE from "three";
import { GameScene, themeFor } from "../game/engine/Scene";

/**
 * Cheap blurred forest scene behind menus. No world / player / shadows —
 * just sky, fog, and a few far-distance grass blocks. ~10x cheaper than the
 * full game scene, capped at 30 FPS, half-resolution, no shadows, no AA.
 *
 * Why no full World/Player here: previously the backdrop was rendering the
 * exact same instanced geometry as the live game, which doubled GPU cost on
 * the menu pages and hurt frame rate.
 */
export function MenuBackdrop() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const theme = themeFor("forest", "afternoon");
    const scene = new GameScene(canvas, theme, {
      skipShadows: true,
      qualityOverride: {
        antialias: false,
        shadowMapSize: 256,
        softShadows: false,
        pixelRatioCap: 0.75,
        hemiLightIntensity: 0.65,
      },
    });

    // Just a single instanced grass tile field + a path strip stretching forward.
    const blockMat = new THREE.MeshStandardMaterial({ color: theme.ground });
    const blockGeo = new THREE.BoxGeometry(2, 2, 2);
    const inst = new THREE.InstancedMesh(blockGeo, blockMat, 240);
    const m = new THREE.Matrix4();
    let i = 0;
    for (let zi = 0; zi < 30; zi++) {
      for (let li = -3; li <= 3; li++) {
        if (Math.abs(li) < 2) continue; // gap for path
        const x = li * 2.2;
        const z = zi * 2.2;
        const y = 0.5 + (Math.sin(zi * 1.7 + li * 0.9) > 0 ? 1 : 0);
        m.makeTranslation(x, y, z);
        inst.setMatrixAt(i++, m);
      }
    }
    inst.count = i;
    inst.instanceMatrix.needsUpdate = true;
    scene.scene.add(inst);

    const path = new THREE.Mesh(
      new THREE.BoxGeometry(4.4, 0.3, 80),
      new THREE.MeshStandardMaterial({ color: theme.pathLight }),
    );
    path.position.set(0, 0.05, 30);
    scene.scene.add(path);

    let raf = 0;
    let last = 0;
    const FRAME_INTERVAL = 1000 / 30; // 30 FPS cap
    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (now - last < FRAME_INTERVAL) return;
      last = now;
      const t = now * 0.0001;
      const z = (now * 0.0008) % 60;
      scene.camera.position.set(Math.sin(t) * 1.2, 6, z - 8);
      scene.camera.lookAt(0, 1, z + 12);
      scene.render();
    };
    const handleResize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      scene.resize(w, h);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleResize);
      scene.dispose();
      blockGeo.dispose();
      blockMat.dispose();
    };
  }, []);

  return <canvas ref={ref} className="menu-backdrop" />;
}
